/**
 * Table State for Poker Reborn
 */

import { signal, computed, Signal } from './Signal';
import type {
  TableSize,
  Street,
  Stakes,
  Card,
  PlayerState,
  PotState,
  ActionType,
  AvailableActions,
  GameInfo,
} from '@/types/game';

// =============================================================================
// LAST ACTION TRACKING (for Street End Animation)
// =============================================================================

/**
 * Information about the last player action on the current street.
 * Used by StreetEndSequencer to determine animation flow.
 *
 * @see docs/systems/STREET_END_ANIMATION.md
 */
export interface LastActionInfo {
  /** Seat number of the player who acted */
  seat: number;
  /** The action type (bet, call, raise, allin, check, fold) */
  action: ActionType;
  /** Amount of the action (0 for check/fold) */
  amount: number;
  /** True if this is a betting action (bet/call/raise/allin) */
  isBettingAction: boolean;
  /** Player ID */
  playerId: string;
}

// =============================================================================
// TABLE STATE
// =============================================================================

export interface TableStateType {
  // Table info
  tableId: Signal<string | null>;
  tableName: Signal<string>;
  tableSize: Signal<TableSize>;
  stakes: Signal<Stakes>;
  gameInfo: Signal<GameInfo | null>;

  // Players
  players: Signal<Map<number, PlayerState>>;

  // Game state
  gameNumber: Signal<number>;
  dealerSeat: Signal<number>;
  activeSeat: Signal<number | null>;
  street: Signal<Street>;
  pots: Signal<PotState[]>;
  communityCards: Signal<Card[]>;
  holeCardCount: Signal<number>;

  // Hero state
  heroSeat: Signal<number | null>;
  heroCards: Signal<Card[] | null>;
  heroActions: Signal<AvailableActions | null>;
  heroSitout: Signal<boolean>;

  // Sitout timers: seat -> seconds remaining
  sitoutTimers: Signal<Map<number, number>>;

  // Last action tracking (for street end animation)
  lastStreetAction: Signal<LastActionInfo | null>;

  // UI state
  isWatching: Signal<boolean>;
  isSeated: Signal<boolean>;

  // Computed
  isHeroTurn: Signal<boolean>;
  totalPot: Signal<number>;
  playerCount: Signal<number>;
}

function createTableState(): TableStateType {
  // Table info
  const tableId = signal<string | null>(null);
  const tableName = signal('');
  const tableSize = signal<TableSize>(9);
  const stakes = signal<Stakes>({ smallBlind: 1, bigBlind: 2 });
  const gameInfo = signal<GameInfo | null>(null);

  // Players
  const players = signal<Map<number, PlayerState>>(new Map());

  // Game state
  const gameNumber = signal(0);
  const dealerSeat = signal(0);
  const activeSeat = signal<number | null>(null);
  const street = signal<Street>('preflop');
  const pots = signal<PotState[]>([]);
  const communityCards = signal<Card[]>([]);
  const holeCardCount = signal(2);

  // Hero state
  const heroSeat = signal<number | null>(null);
  const heroCards = signal<Card[] | null>(null);
  const heroActions = signal<AvailableActions | null>(null);
  const heroSitout = signal(false);

  // Sitout timers: seat -> seconds remaining
  const sitoutTimers = signal<Map<number, number>>(new Map());

  // Last action tracking (for street end animation)
  const lastStreetAction = signal<LastActionInfo | null>(null);

  // UI state
  const isWatching = signal(false);
  const isSeated = computed(() => heroSeat.get() !== null, [heroSeat]);

  // Computed: Is hero's turn
  const isHeroTurn = computed(
    () => {
      const hero = heroSeat.get();
      const active = activeSeat.get();
      return hero !== null && hero === active;
    },
    [heroSeat, activeSeat]
  );

  // Computed: Total pot
  const totalPot = computed(
    () => pots.get().reduce((sum, pot) => sum + pot.amount, 0),
    [pots]
  );

  // Computed: Player count
  const playerCount = computed(
    () => players.get().size,
    [players]
  );

  return {
    tableId,
    tableName,
    tableSize,
    stakes,
    gameInfo,
    players,
    gameNumber,
    dealerSeat,
    activeSeat,
    street,
    pots,
    communityCards,
    holeCardCount,
    heroSeat,
    heroCards,
    heroActions,
    heroSitout,
    sitoutTimers,
    lastStreetAction,
    isWatching,
    isSeated,
    isHeroTurn,
    totalPot,
    playerCount,
  };
}

/** Global table state */
export const tableState = createTableState();

// =============================================================================
// BANKROLL UPDATE TRACKING
// =============================================================================
// Seats that received a `br` message (rebuy/refill) between hands.
// The `end` animation must NOT overwrite their stack — `br` already set the correct value.
// Cleared on next hand start or when leaving the table.

const brUpdatedSeats = new Set<number>();

export function markBrUpdated(seat: number): void {
  brUpdatedSeats.add(seat);
}

export function isBrUpdated(seat: number): boolean {
  return brUpdatedSeats.has(seat);
}

export function clearBrUpdated(): void {
  brUpdatedSeats.clear();
}

// =============================================================================
// ACTIONS
// =============================================================================

/** Join a table (watch mode) */
export function joinTable(
  id: string,
  name: string,
  size: TableSize,
  tableStakes: Stakes
): void {
  tableState.tableId.set(id);
  tableState.tableName.set(name);
  tableState.tableSize.set(size);
  tableState.stakes.set(tableStakes);
  tableState.isWatching.set(true);
  tableState.heroSeat.set(null);
}

/** Leave the table */
export function leaveTable(): void {
  clearBrUpdated();
  tableState.tableId.set(null);
  tableState.tableName.set('');
  tableState.isWatching.set(false);
  tableState.heroSeat.set(null);
  tableState.heroCards.set(null);
  tableState.heroActions.set(null);
  tableState.heroSitout.set(false);
  tableState.sitoutTimers.set(new Map());
  tableState.lastStreetAction.set(null);
  tableState.players.set(new Map());
  tableState.pots.set([]);
  tableState.communityCards.set([]);
  tableState.activeSeat.set(null);
  tableState.street.set('preflop');
}

/** Sit down at a seat */
export function sitDown(seat: number): void {
  tableState.heroSeat.set(seat);
  tableState.heroSitout.set(false);
  tableState.isWatching.set(false);
}

/** Stand up from seat */
export function standUp(): void {
  tableState.heroSeat.set(null);
  tableState.heroCards.set(null);
  tableState.heroActions.set(null);
  tableState.heroSitout.set(false);
  tableState.isWatching.set(true);
}

/** Set hero sitout status */
export function setHeroSitout(isSitout: boolean): void {
  tableState.heroSitout.set(isSitout);
}

// =============================================================================
// SITOUT TIMER ACTIONS
// =============================================================================

/** Set sitout timer for a player */
export function setSitoutTimer(seat: number, seconds: number): void {
  const current = new Map(tableState.sitoutTimers.get());
  current.set(seat, seconds);
  tableState.sitoutTimers.set(current);
}

/** Clear sitout timer for a player */
export function clearSitoutTimer(seat: number): void {
  const current = new Map(tableState.sitoutTimers.get());
  current.delete(seat);
  tableState.sitoutTimers.set(current);
}

/** Clear all sitout timers */
export function clearAllSitoutTimers(): void {
  tableState.sitoutTimers.set(new Map());
}

/** Decrement all sitout timers by 1 second */
export function tickSitoutTimers(): void {
  const current = tableState.sitoutTimers.get();
  if (current.size === 0) return;

  const updated = new Map<number, number>();
  current.forEach((seconds, seat) => {
    const newSeconds = seconds - 1;
    if (newSeconds > 0) {
      updated.set(seat, newSeconds);
    }
    // Timer reaching 0 will be handled by server standup message
  });
  tableState.sitoutTimers.set(updated);
}

/** Get sitout timer for a seat */
export function getSitoutTimer(seat: number): number | undefined {
  return tableState.sitoutTimers.get().get(seat);
}

// =============================================================================
// PLAYER ACTIONS
// =============================================================================

/** Update players */
export function setPlayers(newPlayers: Map<number, PlayerState>): void {
  tableState.players.set(newPlayers);
}

/** Update a single player */
export function updatePlayer(seat: number, player: PlayerState): void {
  const current = new Map(tableState.players.get());
  current.set(seat, player);
  tableState.players.set(current);
}

/** Remove a player */
export function removePlayer(seat: number): void {
  const current = new Map(tableState.players.get());
  current.delete(seat);
  tableState.players.set(current);
}

/** Get player at seat */
export function getPlayer(seat: number): PlayerState | undefined {
  return tableState.players.get().get(seat);
}

/** Set dealer position */
export function setDealer(seat: number): void {
  tableState.dealerSeat.set(seat);
}

/** Set active player */
export function setActivePlayer(seat: number | null): void {
  tableState.activeSeat.set(seat);
}

/** Set street (also clears lastAction for all players) */
export function setStreet(newStreet: Street): void {
  tableState.street.set(newStreet);

  // Clear lastAction for all players on street change
  const players = tableState.players.get();
  const updatedPlayers = new Map(players);
  updatedPlayers.forEach((player, seat) => {
    if (player.lastAction) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { lastAction, ...rest } = player;
      updatedPlayers.set(seat, rest as PlayerState);
    }
  });
  tableState.players.set(updatedPlayers);
}

/** Set community cards */
export function setCommunityCards(cards: Card[]): void {
  tableState.communityCards.set(cards);
}

/** Add community cards */
export function addCommunityCards(cards: Card[]): void {
  const current = tableState.communityCards.get();
  tableState.communityCards.set([...current, ...cards]);
}

/** Set pots */
export function setPots(newPots: PotState[]): void {
  tableState.pots.set(newPots);
}

/** Set hero's hole cards */
export function setHeroCards(cards: Card[] | null): void {
  tableState.heroCards.set(cards);
}

/** Set available actions for hero */
export function setHeroActions(actions: AvailableActions | null): void {
  tableState.heroActions.set(actions);
}

/** Start new hand */
export function startHand(gn: number, dealer: number): void {
  clearBrUpdated();
  tableState.gameNumber.set(gn);
  tableState.dealerSeat.set(dealer);
  tableState.street.set('preflop');
  tableState.communityCards.set([]);
  tableState.pots.set([]);
  tableState.heroCards.set(null);
  tableState.heroActions.set(null);
  tableState.lastStreetAction.set(null);

  // Reset player states (preserve sitout status)
  const currentPlayers = new Map(tableState.players.get());
  currentPlayers.forEach((player, seat) => {
    // Preserve sitout status, reset others to active
    const newStatus = player.status === 'sitout' ? 'sitout' : 'active';
    const updatedPlayer: PlayerState = {
      ...player,
      bet: 0,
      status: newStatus,
      holeCards: null,  // Clear revealed cards from previous hand
      hasCards: false,  // Reset cards flag for new hand
    };
    // Remove lastAction by not including it
    delete (updatedPlayer as { lastAction?: ActionType }).lastAction;
    currentPlayers.set(seat, updatedPlayer);
  });
  tableState.players.set(currentPlayers);
}

/** End hand */
export function endHand(): void {
  tableState.activeSeat.set(null);
  tableState.heroActions.set(null);
  tableState.street.set('showdown');
}

/** Clear table for new hand */
export function clearForNewHand(): void {
  tableState.communityCards.set([]);
  tableState.pots.set([]);
  tableState.heroCards.set(null);
  tableState.heroActions.set(null);
  tableState.street.set('preflop');
  tableState.lastStreetAction.set(null);
}

/** Clear all player bets (used by emulator reset) */
export function clearAllPlayerBets(): void {
  const players = tableState.players.get();
  if (players.size === 0) return;

  const updatedPlayers = new Map(players);
  updatedPlayers.forEach((player, seat) => {
    updatedPlayers.set(seat, { ...player, bet: 0 });
  });
  tableState.players.set(updatedPlayers);
}

// =============================================================================
// LAST ACTION TRACKING (for Street End Animation)
// =============================================================================

/**
 * Set the last action info for the current street.
 * Called after each player action to track what happened.
 *
 * @see docs/systems/STREET_END_ANIMATION.md
 */
export function setLastStreetAction(
  seat: number,
  action: ActionType,
  amount: number,
  playerId: string
): void {
  const isBettingAction = ['bet', 'call', 'raise', 'allin'].includes(action);
  const lastActionInfo: LastActionInfo = {
    seat,
    action,
    amount,
    isBettingAction,
    playerId,
  };
  tableState.lastStreetAction.set(lastActionInfo);
}

/**
 * Clear the last action info.
 * Called after the street end animation sequence completes.
 */
export function clearLastStreetAction(): void {
  tableState.lastStreetAction.set(null);
}

/**
 * Get the current last action info.
 * Returns null if no action has been recorded this street.
 */
export function getLastStreetAction(): LastActionInfo | null {
  return tableState.lastStreetAction.get();
}
